/**
 * InListFilter - Filter toggle for list membership
 *
 * Provides mutually exclusive "In List" / "Not in List" toggles
 * with an optional list dropdown to filter by specific list.
 * Mirrors InDeckFilter pattern.
 */
import type { ReactElement } from 'react';

import Dropdown from '@/components/Dropdown/Dropdown';
import FilterChip from '@/components/FilterChip/FilterChip';
import type { CardList } from '@/types/lists';

export type InListFilterValue = 'in-list' | 'not-in-list';

export interface InListFilterProps {
  /** Current filter value */
  value: InListFilterValue | undefined;
  /** Selected list ID (undefined = any list) */
  listId: string | undefined;
  /** Called when the filter toggle changes */
  onValueChange: (value: InListFilterValue | undefined) => void;
  /** Called when the list selection changes */
  onListChange: (listId: string | undefined) => void;
  /** Available lists to filter by */
  lists: CardList[];
}

export default function InListFilter({ value, listId, onValueChange, onListChange, lists }: InListFilterProps): ReactElement | null {
  // Don't render if there are no lists
  if (lists.length === 0) {
    return null;
  }

  const handleInListClick = () => {
    if (value === 'in-list') {
      // Deselect and clear list
      onValueChange(undefined);
      onListChange(undefined);
    } else {
      // Select "in list", clear any "not in list"
      onValueChange('in-list');
    }
  };

  const handleNotInListClick = () => {
    if (value === 'not-in-list') {
      // Deselect and clear list
      onValueChange(undefined);
      onListChange(undefined);
    } else {
      // Select "not in list", clear any "in list"
      onValueChange('not-in-list');
    }
  };

  const handleListChange = (selectedListId: string | null) => {
    onListChange(selectedListId ?? undefined);
  };

  // Build dropdown options from lists
  const listOptions = lists.map((list) => ({
    value: list.id,
    label: `${list.icon ? list.icon + ' ' : ''}${list.name}`,
  }));

  const isActive = value !== undefined;

  return (
    <div className="flex items-center gap-2">
      <FilterChip label="In List" isActive={value === 'in-list'} onClick={handleInListClick} />
      <FilterChip label="Not in List" isActive={value === 'not-in-list'} onClick={handleNotInListClick} />

      {isActive && (
        <Dropdown
          value={listId ?? null}
          options={listOptions}
          onChange={handleListChange}
          placeholder="Any list"
          allowEmpty
          size="sm"
          aria-label="Select list to filter by"
        />
      )}
    </div>
  );
}
